const API_BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  // 204 No Content (and a few other empty-body cases) have nothing to parse.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return response.json();
}

export const api = {
  // Portfolio / References
  getPortfolio: (filters?: Record<string, string>) => {
    const params = filters ? new URLSearchParams(filters).toString() : '';
    return request(`/portfolio${params ? `?${params}` : ''}`);
  },

  // Participant drilldown
  getParticipant: (id: number) => request(`/participants/${id}`),
  getAttendance: (id: number, from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return request(`/participants/${id}/attendance${qs ? `?${qs}` : ''}`);
  },
  getJournal: (id: number, measureId?: number) => {
    const qs = measureId ? `?measure_id=${measureId}` : '';
    return request(`/participants/${id}/journal${qs}`);
  },
  getReports: (id: number) => request(`/participants/${id}/reports`),
  getActivities: (id: number, measureId?: number) => {
    const qs = measureId ? `?measure_id=${measureId}` : '';
    return request(`/participants/${id}/activities${qs}`);
  },
  getMeasures: (id: number) => request(`/participants/${id}/measures`),
  getTimeline: (id: number) => request(`/participants/${id}/timeline`),

  // Documents (OCR'd PDFs)
  getParticipantDocuments: (participantId: number) =>
    request(`/participants/${participantId}/documents`),
  getMeasureDocuments: (measureId: number) =>
    request(`/measures/${measureId}/documents`),
  getMeasureProgressionPlan: (measureId: number) =>
    request(`/measures/${measureId}/progression-plan`),
  getParticipantProgressionPlans: (participantId: number) =>
    request(`/participants/${participantId}/progression-plans`),
  getParticipantBsp: (participantId: number) =>
    request(`/participants/${participantId}/bsp`),
  getDocument: (docId: number) => request(`/documents/${docId}`),
  documentBlobUrl: (docId: number) => `/api/documents/${docId}/blob`,
  // Raw "fichiers" (file_attachment) — incl. the OAI mandat IP-REA — served over SFTP
  getParticipantFiles: (participantId: number) =>
    request(`/participants/${participantId}/files`),
  participantFileUrl: (participantId: number, fileId: number) =>
    `/api/participants/${participantId}/files/${fileId}/download`,
  documentPageImageUrl: (docId: number, pageNo: number) =>
    `/api/documents/${docId}/page/${pageNo}/image`,

  // Progression-plan adjustments (RM overrides of AI-prescribed plans)
  getMeasureAdjustments: (measureId: number) =>
    request(`/measures/${measureId}/adjustments`),
  deleteMeasureAdjustment: (measureId: number, adjId: number) =>
    request(`/measures/${measureId}/adjustments/${adjId}`, { method: 'DELETE' }),
  createMeasureAdjustment: (
    measureId: number,
    body: {
      progression_type: 'progressive' | 'constant';
      start_pct: number | null;
      target_pct: number;
      period_start: string;
      target_date: string | null;
      qualitative_objectives?: string[];
      reason: string;
      oai_note?: string;
      effective_from?: string;
      to_name?: string;
      to_email?: string;
    },
  ) =>
    request(`/measures/${measureId}/adjustments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Communications (draft emails generated by RM-side actions)
  getAllCommunications: (filters?: { status?: 'unsent' | 'sent' | 'all'; kind?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.kind) params.set('kind', filters.kind);
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return request(`/communications${qs ? `?${qs}` : ''}`);
  },
  getParticipantCommunications: (participantId: number) =>
    request(`/participants/${participantId}/communications`),
  getCommunication: (commId: number) => request(`/communications/${commId}`),
  markCommunicationSent: (commId: number) =>
    request(`/communications/${commId}/mark-sent`, { method: 'POST' }),
  updateCommunication: (commId: number, patch: { subject?: string; body?: string; to_name?: string; to_email?: string }) =>
    request(`/communications/${commId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteCommunication: (commId: number) =>
    request(`/communications/${commId}`, { method: 'DELETE' }),

  // Alerts (stateful notifications)
  getAlerts: (filters?: { status?: 'open' | 'past' | 'all'; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.limit != null) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return request(`/alerts${qs ? `?${qs}` : ''}`);
  },
  requestAlertMute: (alertId: number, reason: string) =>
    request(`/alerts/${alertId}/mute-request`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getMuteRequests: () => request(`/alerts/mute-requests`),
  approveMuteRequest: (id: number, note?: string) =>
    request(`/alerts/mute-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ note: note ?? '' }) }),
  denyMuteRequest: (id: number, note?: string) =>
    request(`/alerts/mute-requests/${id}/deny`, { method: 'POST', body: JSON.stringify({ note: note ?? '' }) }),

  // AI
  journalSynthesis: (measureId: number, opts?: { months?: number; dateFrom?: string; dateTo?: string }) =>
    request('/ai/journal-synthesis', { method: 'POST', body: JSON.stringify({ measure_id: measureId, months: opts?.months, date_from: opts?.dateFrom, date_to: opts?.dateTo }) }),
  bilanDraft: (measureId: number, periodStart?: string, periodEnd?: string) =>
    request('/ai/bilan-draft', { method: 'POST', body: JSON.stringify({ measure_id: measureId, period_start: periodStart, period_end: periodEnd }) }),
  attendanceAnalysis: (participantId: number, measureId?: number, opts?: { dateFrom?: string; dateTo?: string }) =>
    request('/ai/attendance-analysis', { method: 'POST', body: JSON.stringify({ participant_id: participantId, measure_id: measureId, date_from: opts?.dateFrom, date_to: opts?.dateTo }) }),

  // Roles & capabilities
  getRoleInfo: () => request('/roles/me'),
  setLens: (lens: string) =>
    request('/roles/me/lens', { method: 'PUT', body: JSON.stringify({ lens }) }),
  getRoleUsers: () => request('/roles/users'),
  updateUserCaps: (userId: number, caps: Partial<Record<string, boolean>>) =>
    request(`/roles/users/${userId}`, { method: 'PUT', body: JSON.stringify(caps) }),
  getPermissionsOverview: () => request('/roles/permissions'),
  getNotificationMeta: () => request('/roles/notification-meta'),

  // Equipe
  getEquipe: () => request('/equipe'),

  // Conseillers
  getConseillers: () => request('/conseillers'),
  getConseillerDetail: (contactId: number) => request(`/conseillers/${contactId}`),
  getConseillerRevenue: (contactId: number) => request(`/conseillers/${contactId}/revenue`),

  // Lookups
  getMeasureTypes: () => request('/lookups/measure-types'),
  getMeasureStatuses: () => request('/lookups/measure-statuses'),
  getOrganizations: () => request('/lookups/organizations'),
  getStaff: () => request('/lookups/staff'),
  getGoals: () => request('/lookups/goals'),

  // Admin
  getSettings: () => request('/admin/settings'),
  updateSetting: (key: string, value: string) =>
    request(`/admin/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  // V2.1 identity view (session users ↔ V2.1 user_account + native roles)
  getIdentity: () => request('/admin/identity'),
  getFunderLens: () => request('/admin/funder-lens'),
  updateFunderLens: (funderId: number, lens: 'oai' | 'ri' | null) =>
    request(`/admin/funder-lens/${funderId}`, { method: 'PUT', body: JSON.stringify({ lens }) }),
  getStats: () => request('/admin/stats'),

  // Notifications
  getNotifications: () => request('/notifications'),
  getUnreadCount: () => request<{ count: number }>('/notifications/unread-count'),
  markNotificationRead: (id: number) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
  markAllNotificationsRead: () => request('/notifications/mark-all-read', { method: 'POST' }),
  getNotificationPreferences: () => request('/notifications/preferences'),
  updateNotificationPreference: (notification_type: string, enabled: boolean) =>
    request('/notifications/preferences', { method: 'PUT', body: JSON.stringify({ notification_type, enabled }) }),

  // Password
  changePassword: (currentPassword: string, newPassword: string) =>
    request('/admin/users/me/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),

  // Profile (app-level preferences: language, workspace snapshot)
  getProfile: () => request<{ id: number; name: string | null; email: string | null; lang: 'fr' | 'en' | null; workspace?: unknown }>('/profile'),
  updateProfile: (patch: { lang: 'fr' | 'en' }) =>
    request('/profile', { method: 'PATCH', body: JSON.stringify(patch) }),
  setWorkspace: (workspace: unknown) =>
    request('/profile/workspace', { method: 'PUT', body: JSON.stringify(workspace) }),

  // Generic helpers
  get: <T = any>(path: string) => request<T>(path),
  post: (path: string, data: any) => request(path, { method: 'POST', body: JSON.stringify(data) }),
  put: (path: string, data: any) => request(path, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (path: string, data: any) => request(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (path: string) => request(path, { method: 'DELETE' }),
};
